import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "planCard", "planName", "planPrice", "planDescription", "planFeatures", "planCommitment", "planBadge", "planPriceLabel"]

  connect() {
    this.setActiveButton("all-features")
    // Initialize price labels on page load
    this.maintainPriceLabels()
  }

  switchToAllFeatures(event) {
    event.preventDefault()
    this.setActiveButton("all-features")
    this.updatePlans("all")
  }

  switchToDocumentFraud(event) {
    event.preventDefault()
    this.setActiveButton("document-fraud")
    this.updatePlans("document")
  }

  switchToIdentityVerification(event) {
    event.preventDefault()
    this.setActiveButton("identity-verification")
    this.updatePlans("identity")
  }

  setActiveButton(activeId) {
    this.buttonTargets.forEach(button => {
      if (button.dataset.productType === activeId) {
        button.classList.add("active")
        button.classList.remove("inactive")
      } else {
        button.classList.remove("active")
        button.classList.add("inactive")
      }
    })
  }

  updatePlans(productType) {
    this.planCardTargets.forEach((card, index) => {
      const dataKey = `${productType}Data`
      const planData = JSON.parse(card.dataset[dataKey] || '{}')
      
      if (planData.name) {
        // Check if this card is the current plan (has bg-primary class)
        const isCurrentPlan = card.querySelector('.card.bg-primary') !== null
        const originalPlanName = card.dataset.planName
        
        // Find elements within the card
        const nameElement = card.querySelector('[data-plans-switcher-target="planName"]')
        const priceElement = card.querySelector('[data-plans-switcher-target="planPrice"]')
        const priceLabelElement = card.querySelector('[data-plans-switcher-target="planPriceLabel"]')
        const descriptionElement = card.querySelector('[data-plans-switcher-target="planDescription"]')
        const commitmentElement = card.querySelector('[data-plans-switcher-target="planCommitment"]')
        const badgeElement = card.querySelector('[data-plans-switcher-target="planBadge"]')
        const featuresElement = card.querySelector('[data-plans-switcher-target="planFeatures"]')
        
        if (nameElement) nameElement.textContent = planData.name
        
        if (priceElement) {
          const textClass = this.getPlanTextClass(originalPlanName, isCurrentPlan)
          
          // Clear the wrapper first
          const wrapper = priceElement.parentElement
          if (wrapper && wrapper.classList.contains('plan-price-wrapper')) {
            wrapper.innerHTML = ''
            
            // Recreate the h1 element with proper classes
            const newPriceElement = document.createElement('h1')
            newPriceElement.className = `mt-3 ${textClass}`
            newPriceElement.setAttribute('data-plans-switcher-target', 'planPrice')
            
            if (planData.price_unit) {
              newPriceElement.innerHTML = `${planData.price}<span class="price-unit ${textClass}"> ${planData.price_unit}</span>`
            } else if (originalPlanName !== "Enterprise") {
              // For "all features" view, maintain the original format
              newPriceElement.innerHTML = `${planData.price}<span class="price-unit ${textClass}"> / ID verification</span>`
            } else {
              newPriceElement.innerHTML = planData.price
            }
            
            wrapper.appendChild(newPriceElement)
          }
        }
        
        if (descriptionElement) descriptionElement.textContent = planData.description
        
        // Price labels are handled by maintainPriceLabels() method
        
        if (commitmentElement) {
          if (planData.commitment) {
            // Add help icon for non-Enterprise plans
            if (originalPlanName !== "Enterprise") {
              commitmentElement.innerHTML = `${planData.commitment}<a class="help-icon" tabindex="0" role="button" data-bs-toggle="popover" data-bs-placement="top" data-bs-trigger="hover" title="${originalPlanName} plan" data-bs-content="The minimum amount that you pay each month. Once you exceed this amount, you will be charged for all additional checks."><i class="fa-regular fa-circle-info"></i></a>`
            } else {
              commitmentElement.innerHTML = planData.commitment
            }
          } else if (originalPlanName === "Enterprise") {
            // Special case for Enterprise in identity verification view
            commitmentElement.innerHTML = "1,000+ monthly verifications"
          }
        }

        if (badgeElement) {
          const badgeContainer = badgeElement.parentElement
          if (planData.badge) {
            badgeElement.textContent = planData.badge
            badgeContainer.style.display = "block"
            // Update badge class based on plan name
            this.updateBadgeClass(badgeElement, originalPlanName)
          } else {
            // Check if this is the "all features" view and restore default badges
            if (productType === "all") {
              badgeContainer.style.display = "block"
              switch(originalPlanName) {
                case "Core":
                  badgeElement.textContent = "Essential"
                  this.updateBadgeClass(badgeElement, "Core")
                  break
                case "Compliance":
                  badgeElement.textContent = "Popular"
                  this.updateBadgeClass(badgeElement, "Compliance")
                  break
                case "Forensic":
                  badgeElement.textContent = "Fraudfighter"
                  this.updateBadgeClass(badgeElement, "Forensic")
                  break
                case "Enterprise":
                  badgeElement.textContent = "All features"
                  this.updateBadgeClass(badgeElement, "Enterprise")
                  break
                default:
                  badgeContainer.style.display = "none"
              }
            } else {
              // Hide badge if no badge data for other views
              badgeContainer.style.display = "none"
            }
          }
        }

        if (featuresElement && planData.features) {
          featuresElement.innerHTML = ""
          planData.features.forEach(feature => {
            const p = document.createElement("p")
            p.className = "mb-3 font-size-15"
            p.innerHTML = `<i class="${this.getFeatureIconClass(originalPlanName, isCurrentPlan)}"></i>${feature}`
            featuresElement.appendChild(p)
          })
        }
      }
    })
    
    // Maintain price labels after view switch
    this.maintainPriceLabels()
    
    // Reinitialize popovers after updating the DOM
    this.initializePopovers()
  }
  
  initializePopovers() {
    // Clean up existing popovers
    const existingPopovers = document.querySelectorAll('[data-bs-toggle="popover"]')
    existingPopovers.forEach(el => {
      const popover = bootstrap.Popover.getInstance(el)
      if (popover) popover.dispose()
    })
    
    // Initialize new popovers
    const popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'))
    popoverTriggerList.map(function (popoverTriggerEl) {
      return new bootstrap.Popover(popoverTriggerEl)
    })
  }

  getFeatureIconClass(planName, isCurrentPlan) {
    if (isCurrentPlan) {
      return "i fa-kit fa-check-circle check-icon text-light font-size-18 me-2"
    } else {
      return "i fa-kit fa-check-circle check-icon text-secondary font-size-18 me-2"
    }
  }
  
  getPlanTextClass(planName, isCurrentPlan) {
    // Match the Ruby helper plan_text_classes method
    if (isCurrentPlan) {
      return "text-white"
    } else {
      return "text-muted"
    }
  }

  maintainPriceLabels() {
    // Get i18n translations from data attributes
    const moreChecks = this.element.dataset.moreChecks || "more checks"
    const evenMoreChecks = this.element.dataset.evenMoreChecks || "even more checks"
    
    this.planCardTargets.forEach((card) => {
      const planName = card.dataset.planName
      const priceLabelElement = card.querySelector('[data-plans-switcher-target="planPriceLabel"]')
      
      if (!priceLabelElement) return
      
      // Determine if this plan should have a label based on plan name
      let shouldShowLabel = false
      let labelText = ""
      let isEvenMore = false
      
      if (planName && planName.toLowerCase().includes("compliance")) {
        shouldShowLabel = true
        labelText = moreChecks
        isEvenMore = false
      } else if (planName && planName.toLowerCase().includes("forensic")) {
        shouldShowLabel = true
        labelText = evenMoreChecks
        isEvenMore = true
      }
      
      if (shouldShowLabel) {
        this.updatePriceLabel(priceLabelElement, labelText, isEvenMore)
        priceLabelElement.style.display = "flex"
      } else {
        priceLabelElement.style.display = "none"
      }
    })
  }

  updatePriceLabel(element, priceLabel, isEvenMore) {
    // Create the modern price label structure
    const labelClass = isEvenMore ? "price-label-premium" : "price-label-standard"
    const iconClass = isEvenMore ? "fa-solid fa-arrow-trend-up" : "fa-solid fa-plus"
    
    // Ensure the wrapper structure is maintained
    element.innerHTML = `
      <div class="price-label ${labelClass}">
        <i class="${iconClass} me-1"></i>
        <span>${priceLabel}</span>
      </div>
    `
    element.classList.add("price-label-wrapper", "mt-2")
  }

  updateBadgeClass(badgeElement, planName) {
    // Remove existing badge classes
    badgeElement.classList.remove("badge-essential", "badge-popular", "badge-fraudfighter", "badge-all-features", "bg-secondary")
    
    // Add the appropriate badge class based on plan name
    switch(planName) {
      case "Core":
        badgeElement.classList.add("badge-essential")
        break
      case "Compliance":
        badgeElement.classList.add("badge-popular")
        break
      case "Forensic":
        badgeElement.classList.add("badge-fraudfighter")
        break
      case "Enterprise":
        badgeElement.classList.add("badge-all-features")
        break
      default:
        badgeElement.classList.add("bg-secondary")
    }
  }
}